import { Injectable } from '@angular/core';
import {
  ChartConfiguration,
  ChartDataset,
  ElementOptionsByType,
  PluginOptionsByType,
  ScaleChartOptions,
  TooltipOptions,
} from 'chart.js';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { TrialInsightsChartModel } from '../../models/trial-insights-chart.model';

export type TIFPEChartLabel = [string, number][];
export type TIFPEChartType = 'bar';

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsFinancePatientsEnrolledChartService
  implements TrialInsightsChartModel<TIFPEChartType, TIFPEChartLabel>
{
  elementOptions = (): DeepPartial<ElementOptionsByType<TIFPEChartType>> => {
    return {
      bar: {
        borderColor: '#bacad0',
      },
    };
  };

  tooltipOptions = (): DeepPartial<TooltipOptions<TIFPEChartType>> => {
    return {
      callbacks: {
        label: (tooltipItem) => {
          return `${tooltipItem.label}: ${tooltipItem.raw}%`;
        },
      },
    };
  };

  pluginOptions = (): DeepPartial<PluginOptionsByType<TIFPEChartType>> => {
    return {
      legend: {
        display: false,
      },
      tooltip: this.tooltipOptions(),
    };
  };

  xAxesOptions = (maxXAxes?: number): DeepPartial<ScaleChartOptions<TIFPEChartType>> => {
    return {
      scales: {
        x1: {
          beginAtZero: true,
          grid: {
            color: '#bacad0',
            display: false,
            lineWidth: 0.75,
            z: 1,
          },
          max: maxXAxes,
          stacked: true,
          ticks: {
            display: false,
            stepSize: 10,
          },
          title: {
            display: false,
          },
        },
      },
    };
  };

  yAxesOptions = (): DeepPartial<ScaleChartOptions<TIFPEChartType>> => {
    return {
      scales: {
        y: {
          display: false,
          stacked: true,
        },
      },
    };
  };

  chartOptions = (maxXAxes?: number): ChartConfiguration<TIFPEChartType>['options'] => {
    return {
      elements: this.elementOptions(),
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: this.pluginOptions(),
      responsive: true,
      scales: {
        ...this.xAxesOptions(maxXAxes).scales,
        ...this.yAxesOptions().scales,
      },
    };
  };

  labelOptions = () => {
    return ['Patients Enrolled'];
  };

  createChart = (
    datasets?: ChartDataset<TIFPEChartType>[],
    maxXAxes = 100
  ): ChartConfiguration<TIFPEChartType> => {
    return {
      data: {
        datasets: datasets ? datasets : [],
        labels: this.labelOptions(),
      },
      options: this.chartOptions(maxXAxes),
      type: 'bar',
    };
  };

  createDatasets = (labels: TIFPEChartLabel) => {
    const datasets: ChartDataset<TIFPEChartType>[] = [];

    if (!labels?.length) {
      return datasets;
    }

    const colors = ['#138EE7'];
    const hoverColors = ['#138EE7'];

    labels.forEach(([label, percent], index) => {
      datasets.push({
        backgroundColor: [colors[index]],
        barThickness: 85,
        data: [percent],
        hoverBackgroundColor: [hoverColors[index]],
        label: label,
        xAxisID: 'x1',
      });
    });

    return datasets;
  };
}
